import { makeObservable, observable, action } from 'mobx';
import {
  CourseConcise,
  CourseInfo,
  AuthState,
  RatingField,
  Review,
  User,
} from '../types';
import { storeData, getStoreData, removeStoreItem } from '../helpers/store';
import {
  HISTORY_MAX_LENGTH,
  LEVEL_UP_EXP,
  TOKEN_EXPIRE_BEFORE,
} from '../config';
import withUndo from '../helpers/withUndo';
import { getTokenExpireDate } from '../helpers';
import { TOKEN_EXPIRE_DAYS } from '../constants';
import ViewStore from './ViewStore';
import StorePrototype from './StorePrototype';
import PlannerStore from './PlannerStore';

const LOAD_KEYS = [
  'favoriteCourses',
  'searchHistory',
  'reviewDrafts',
  'recentReviewCategory',
];

const RESET_KEYS = [...LOAD_KEYS, 'token'];

const DEFAULT_VALUES = {
  reviewDrafts: {},
  searchHistory: [],
  favoriteCourses: [],
  loginState: AuthState.INIT,
  recentReviewCategory: 'grading',
};

const LOGOUT_KEYS = ['username', 'token'];

class UserStore extends StorePrototype {
  // General State
  @observable loginState: AuthState;

  // User Saved Data
  @observable reviewDrafts: Record<string, Review> = {};
  @observable searchHistory: string[] = [];
  @observable favoriteCourses: CourseConcise[] = [];
  @observable recentReviewCategory: RatingField;

  // User Session Data (Reload everytime init)
  @observable data: User;

  // CUtopia
  @observable username: string;
  @observable token: string;

  viewStore: ViewStore;
  plannerStore: PlannerStore;

  constructor(viewStore: ViewStore) {
    super(LOAD_KEYS, RESET_KEYS, DEFAULT_VALUES);
    this.viewStore = viewStore;
    makeObservable(this);
  }

  @action setPlannerStore = (store: PlannerStore) => {
    this.plannerStore = store;
  };

  @action init() {
    this.loadStore();
    this.applyToken();
    if (this.loginState === AuthState.INIT) {
      this.loginState = AuthState.LOGGED_OUT;
    }
  }

  get level() {
    return Math.floor((this.data?.exp || 0) / LEVEL_UP_EXP);
  }

  get loggedIn() {
    return Boolean(this.data?.username);
  }

  @action saveUser = async (
    username: string,
    token: string,
    data: Partial<User>
  ) => {
    // need set date before set token cuz set token first will trigger query user.me
    this.updateStore('data', data);
    this.saveToken(token);
    const migrated = await this.connectPlanner();
    if (migrated !== null) {
      this.viewStore.setSnackBar(
        migrated
          ? 'Signed in — your timetable is now synced'
          : `Logged in as ${username || data?.username}`
      );
    }
  };

  @action updateUserData = (data: Partial<User>) => {
    if (data?.username) {
      this.updateStore('data', data);
      // Returning users already have cloud state. Only an explicit login via
      // saveUser should migrate a guest timetable from this device.
      this.plannerStore.setOnline();
    }
  };

  private connectPlanner = async (): Promise<boolean | null> => {
    try {
      return await this.plannerStore.connectAuthenticated();
    } catch {
      // Authentication succeeded even if syncing did not. Keep the local copy
      // and continue with the user's existing cloud planner.
      this.plannerStore.setOnline();
      this.viewStore.warn(
        'Signed in. Your on-device timetable is safe; sign out and back in to retry syncing.'
      );
      return null;
    }
  };

  @action applyToken = () => {
    const savedToken = getStoreData('token') || {};
    if (!savedToken.token) return;
    if (
      savedToken.expire <
      getTokenExpireDate(TOKEN_EXPIRE_BEFORE, TOKEN_EXPIRE_DAYS)
    ) {
      removeStoreItem('token');
    } else {
      this.setToken(savedToken.token);
    }
  };

  @action saveToken = (token: string) => {
    const savedToken = {
      token,
      expire: getTokenExpireDate(+new Date()),
    };
    storeData('token', savedToken);
    this.setToken(token);
  };

  @action setToken = (token: string) => {
    this.token = token;
    this.loginState = AuthState.LOGGED_IN;
  };

  @action logout = () => {
    this.setLogout();
    LOGOUT_KEYS.forEach(key => {
      removeStoreItem(key);
      this[key] = undefined;
    });
    this.data = undefined;
  };

  @action setLogout = () => {
    this.loginState = AuthState.LOGGED_OUT;
    this.token = null;
    this.plannerStore?.setOffline();
  };

  // Fav courses
  @action checkIsFavourite = (courseId: string) =>
    this.favoriteCourses.some(course => course.courseId === courseId);

  @action toggleFavourite = (courseInfo: CourseInfo, isFavourite?: boolean) => {
    if (isFavourite || this.checkIsFavourite(courseInfo.courseId)) {
      this.setStore(
        'favoriteCourses',
        [...this.favoriteCourses].filter(
          course => course.courseId !== courseInfo.courseId
        )
      );
    } else {
      this.setStore(
        'favoriteCourses',
        this.favoriteCourses.concat({
          courseId: courseInfo.courseId,
          title: courseInfo.title,
        })
      );
    }
  };

  // Search History
  @action saveHistory = (courseId: string) => {
    let temp = [...this.searchHistory];
    if (temp.length >= HISTORY_MAX_LENGTH) {
      temp.pop();
    }
    temp = [courseId].concat(temp.filter(saved => saved !== courseId));
    this.setStore('searchHistory', temp);
  };

  @action updateReviewDrafts = (courseId: string, review: Review) => {
    withUndo(
      {
        prevData: { ...this.reviewDrafts },
        setData: prevData => this.setStore('reviewDrafts', prevData),
        message: 'Draft saved!',
        viewStore: this.viewStore,
      },
      () => {
        const copy = { ...this.reviewDrafts };
        copy[courseId] = review;
        this.setStore('reviewDrafts', copy);
      }
    );
  };

  @action deleteReveiwDraft = (courseId: string) => {
    const copy = { ...this.reviewDrafts };
    delete copy[courseId];
    this.setStore('reviewDrafts', copy);
  };
}

export default UserStore;
